"use client";

import { useMemo, useState } from "react";
import styles from "./GameReferenceCatalog.module.css";
import { emitUsageEvent } from "../lib/umami";

export interface ReferenceImage {
  src: string;
  alt: string;
  width: number;
  height: number;
}
export interface ReferenceLink { label: string; href: string }
export interface ReferenceCell {
  text: string;
  images?: ReferenceImage[];
  links?: ReferenceLink[];
  color?: string;
  items?: string[];
}
export interface ReferenceSection {
  id: string;
  title: string;
  columns: string[];
  rows: { id: string; cells: ReferenceCell[] }[];
}
export interface GameReferenceData {
  title: string;
  sourceUrl: string;
  retrievedAt: string;
  downloadUrl: string;
  sections: ReferenceSection[];
}

export default function GameReferenceCatalog({ data, pageSize = 40 }: {
  data: GameReferenceData;
  pageSize?: number;
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("");
  const size = Math.max(1, Math.floor(pageSize) || 40);
  const indexed = useMemo(() => data.sections.flatMap((section) => section.rows.map((row) => ({
    section,
    row,
    text: [section.title, ...row.cells.flatMap((cell) => [
      cell.text, cell.color ?? "", ...(cell.items ?? []),
      ...(cell.links ?? []).map((link) => link.label),
      ...(cell.images ?? []).map((image) => image.alt),
    ])].join(" ").toLowerCase(),
  }))), [data]);
  const matches = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return indexed.filter((item) => (!category || item.section.id === category)
      && words.every((word) => item.text.includes(word)));
  }, [indexed, query, category]);
  const pageCount = Math.max(1, Math.ceil(matches.length / size));
  const current = Math.min(page, pageCount - 1);
  const visible = matches.slice(current * size, (current + 1) * size);
  const groups = data.sections.map((section) => ({
    section,
    rows: visible.filter((item) => item.section.id === section.id).map((item) => item.row),
  })).filter((group) => group.rows.length);

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      emitUsageEvent("example_copy");
      setStatus(`Copied ${text}`);
    } catch {
      setStatus("Clipboard unavailable. Select and copy the displayed value instead.");
    }
  }

  return (
    <section className={styles.catalog} aria-label={`${data.title} catalog`}>
      <p className={styles.provenance}>
        <a href={data.sourceUrl}>Official source</a>{" · "}
        Snapshot: <time dateTime={data.retrievedAt}>{data.retrievedAt}</time>{" · "}
        <a href={data.downloadUrl} download>Download complete JSON</a>
      </p>
      <div className={styles.toolbar}>
        <label className={styles.search}>
          <span>Search {data.title}</span>
          <input type="search" value={query} placeholder="Name, ID, hash, component…"
            onChange={(event) => { setQuery(event.target.value); setPage(0); }} />
        </label>
        {data.sections.length > 1 && <label>
          <span>Category</span>
          <select value={category} onChange={(event) => { setCategory(event.target.value); setPage(0); }}>
            <option value="">All categories</option>
            {data.sections.map((section) => <option key={section.id} value={section.id}>
              {section.title} ({section.rows.length})
            </option>)}
          </select>
        </label>}
      </div>
      <p className={styles.count} aria-live="polite">{matches.length} of {indexed.length} entries</p>
      {groups.map(({ section, rows }) => <div key={section.id} className={styles.group}>
        <div className={styles.scroller} tabIndex={0} role="region" aria-label={`${section.title} reference table`}>
          <table>
            <caption>{section.title}</caption>
            <thead><tr>{section.columns.map((column, index) => <th key={`${index}-${column}`} scope="col">{column}</th>)}</tr></thead>
            <tbody>{rows.map((row) => <tr key={row.id}>
              {row.cells.map((cell, index) => <td key={index}>
                {cell.images?.map((image) => <a key={image.src} href={image.src} target="_blank" rel="noopener noreferrer"
                  className={styles.preview} aria-label={`Open full-size ${image.alt}`}>
                  {/* Preserve original GIF animation and serve verified local bytes without a third-party request. */}
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={image.src} alt={image.alt} width={image.width} height={image.height} loading="lazy" decoding="async" />
                </a>)}
                {cell.color && <span className={styles.swatch} style={{ backgroundColor: cell.color }} aria-label={`Color ${cell.color}`} />}
                {cell.text && <span className={styles.value}>{cell.text}</span>}
                {cell.items && cell.items.length > 0 && <details>
                  <summary>{cell.items.length} {section.columns[index].toLowerCase()}</summary>
                  <ul>{cell.items.map((item, itemIndex) => <li key={`${itemIndex}-${item}`}>{item}</li>)}</ul>
                </details>}
                {cell.links?.map((link, linkIndex) => <a className={styles.referenceLink} key={`${linkIndex}-${link.href}`} href={link.href}>{link.label || "Reference"}</a>)}
                {cell.text && /hash|model|^id$|^index$|key|^name$/i.test(section.columns[index]) && cell.text.length < 150 && <button
                  type="button" className={styles.copy} onClick={() => copy(cell.text)} aria-label={`Copy ${cell.text}`} title="Copy value">⧉</button>}
              </td>)}
            </tr>)}</tbody>
          </table>
        </div>
      </div>)}
      {matches.length === 0 && <p>No matching entries. Try a model name, numeric ID, or component name.</p>}
      {pageCount > 1 && <nav className={styles.pagination} aria-label="Catalog pagination">
        <button type="button" disabled={current === 0} onClick={() => setPage(0)}>First</button>
        <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous</button>
        <span>Page {current + 1} of {pageCount}</span>
        <button type="button" disabled={current === pageCount - 1} onClick={() => setPage(current + 1)}>Next</button>
        <button type="button" disabled={current === pageCount - 1} onClick={() => setPage(pageCount - 1)}>Last</button>
      </nav>}
      <p className={styles.status} role="status">{status}</p>
      <noscript>The first page is shown above. The complete JSON download includes every entry without JavaScript.</noscript>
    </section>
  );
}
